/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextareaAutosize from "@mui/material/TextareaAutosize";

import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";
import GridDropdown from "../../widgets/GridDropdown";
import GridCheckbox from "../../widgets/GridCheckbox";

import { reNumbers, reEmail, alphanum } from "../../../utils/fieldValidator.js";
import countryPhoneCode from "../../../data/countryPhoneCodes.json";

import PageNavigator, { onNext } from "../../PageNavigator";

import { MAX_LENGTH_TEXTAREA, IDENTITY_TYPE_DATA } from "../../../utils/constants.js";

let isSelectedClientError = "";
let isAccompaniedError = "";
var trusteeReasonError = "";
let trusteeFullNameError = "";
let trusteeDobError = "";
let trusteeIdTypeError = "";
let trusteeIdError = "";
let trusteeNationalityError = "";
let trusteeMobileCodeError = "";
let trusteeMobileNumberError = "";
let trusteeEmailError = "";
let trusteeRelationshipError = "";
let trusteeLanguageError = "";
let trusteeAcknowledgeError = "";

export default function TrustedIndividual() {
  //FORM VALIDATATION STATUS
  const [isValid, setIsValid] = useState(false);

  //LOAD DATA FROM GLOBAL DATA MANAGER
  const currApplicant = useSelector((state) => state.app.currApplicant);
  const personalInfo = useSelector((state) => state.personalInformation[currApplicant]);

  const fullName = personalInfo.fullName;
  const [isSelectedClient, setIsSelectedClient] = useState("");
  const [isAccompanied, setIsAccompanied] = useState("");
  const [trusteeReason, setTrusteeReason] = useState("");

  const [trusteeFullName, setTrusteeFullName] = useState("");
  const [trusteeDOB, setTrusteeDOB] = useState(dayjs());
  const [trusteeIdType, setTrusteeIdType] = useState("");
  const [trusteeId, setTrusteeId] = useState("");
  const [trusteeNationality, setTrusteeNationality] = useState("");
  const [trusteeMobileCode, setTrusteeMobileCode] = useState("");
  const [trusteeMobileNumber, setTrusteeMobileNumber] = useState("");
  const [trusteeEmail, setTrusteeEmail] = useState("");
  const [trusteeRelationship, setTrusteeRelationship] = useState("");
  const [trusteeLanguage, setTrusteeLanguage] = useState("");
  const [trusteeAcknowledge, setTrusteeAcknowledge] = useState("");

  //COMPONENT ID
  let id = "";

  //CALLED WHEN "NEXT BUTTON" IS PRESSED. WHEN ATTEMPTING TO MOVE TO NEXT PAGE.
  function validateForm(e) {
    e.preventDefault();

    if (isAccompanied === "NO") {
      if (trusteeReason.length === 0) {
        trusteeReasonError = "Please provide a reason";
      } else if (trusteeReason.length > MAX_LENGTH_TEXTAREA) {
        trusteeReasonError = "Exceeded 1000 characters";
      } else {
        trusteeReasonError = "";
        setIsValid(true);
        onNext();
      }
    } else if (isAccompanied === "YES") {
      if (isSelectedClient === "")
        isSelectedClientError = "Please acknowledge that you have read and understood the above.";
      else isSelectedClientError = "";

      if (isAccompanied === "") isAccompaniedError = "Please indicate trustee.";
      else isAccompaniedError = "";

      //TRUSTEE INFORMATION
      if (trusteeFullName.length === 0) trusteeFullNameError = "Full name is required.";
      else if (trusteeFullName.length > 70) trusteeFullNameError = "Full name exceeded 70 characters";
      else trusteeFullNameError = "";

      if (trusteeIdType === "") trusteeIdTypeError = "Identity type is required.";
      else trusteeIdTypeError = "";

      if (trusteeNationality === "") trusteeNationalityError = "Nationality is required.";
      else trusteeNationalityError = "";

      if (trusteeMobileCode.length === 0) trusteeMobileCodeError = "Country code is required.";
      else trusteeMobileCodeError = "";

      if (trusteeId.length === 0) trusteeIdError = "Identity number is required.";
      else if (trusteeId.length > 30) trusteeIdError = "Identity number is more than 30 charracterss.";
      else trusteeIdError = "";

      if (trusteeMobileNumber.length === 0) trusteeMobileNumberError = "Mobile number is required.";
      else if (trusteeMobileNumber.length > 12) trusteeMobileNumberError = "Mobile number exceeded 12 characters.";
      else trusteeMobileNumberError = "";

      if (trusteeEmail.length === 0) trusteeEmailError = "Email is required.";
      else if (trusteeEmail.length > 30) trusteeEmailError = "Email exceeded 30 characters.";
      else trusteeEmailError = "";

      if (trusteeRelationship.length === 0) trusteeRelationshipError = "Relationship description is required.";
      else if (trusteeRelationship.length > 30)
        trusteeRelationshipError = "Relationship description exceeded 30 characters.";
      else trusteeRelationshipError = "";

      if (trusteeLanguage.length === 0) trusteeLanguageError = "Language is required.";
      else if (trusteeLanguage.length > 30) trusteeLanguageError = "Language exceeded 30 characters.";
      else trusteeLanguageError = "";

      if (trusteeAcknowledge === "") trusteeAcknowledgeError = "Acknowledgement required";
      else trusteeAcknowledgeError = "";

      if (
        isAccompaniedError === "" &&
        trusteeFullNameError === "" &&
        trusteeIdTypeError === "" &&
        trusteeIdError === "" &&
        trusteeNationalityError === "" &&
        trusteeMobileCodeError === "" &&
        trusteeMobileNumberError === "" &&
        trusteeEmailError === "" &&
        trusteeRelationshipError === "" &&
        trusteeLanguageError === "" &&
        trusteeAcknowledgeError === ""
      ) {
        setIsValid(true);
        onNext();
      }
    }

    setIsValid(false);
  }

  /**
   * 1. VALIDITE FORM USING ERROR MESSAGES
   *     "CALCULATE VALIDITY" IS SIMILAR TO "VALIDATE FORM" IT VALIDATES BY ERROR MESSAGE.
   *       WHEN ALL FIELD'S ERROR MESSAGES ARE EMPTY. (VALID = TRUE)
   *
   * 2. THIS FUNCTION IS ALSO RESPONSIBLE FOR ENABLING / DISABLING THE NEXT BUYTTON, THEREFORE IS TRIGGERED BY EACH UI EVENT
   */
  function calculateValidity() {
    if (
      trusteeReasonError === "" &&
      trusteeFullNameError === "" &&
      trusteeDobError === "" &&
      trusteeIdTypeError === "" &&
      trusteeIdError === "" &&
      trusteeNationalityError === "" &&
      trusteeMobileCodeError === "" &&
      trusteeMobileNumberError === "" &&
      trusteeEmailError === "" &&
      trusteeRelationshipError === "" &&
      trusteeLanguageError === "" &&
      trusteeAcknowledgeError === ""
    ) {
      setIsValid(true);
    }
  }

  function handleChange(e, object) {
    id = "";

    if (e instanceof PointerEvent) {
      id = object.props.id.toLowerCase();

      //TRUSTEE INFORMATION
      if (id === "trusteeidtype") {
        setTrusteeIdType(e.target.value);
        trusteeIdTypeError = "";
      }

      if (id === "trusteenationality") {
        setTrusteeNationality(e.target.value);
        trusteeNationalityError = "";
      }

      if (id === "trusteemobilecode") {
        setTrusteeMobileCode(e.target.value);
        trusteeMobileCodeError = "";
      }
    } else {
      let type = "";
      let name = "";

      id = e.target.id;

      if (id === "") {
        type = e.target.type;
        name = e.target.name;
      }

      //SELECTED CLIENT - ACKNOWLEDGE
      if (id === "selectedClient") {
        setIsSelectedClient(e.target.checked);
        isSelectedClientError = "";
      }

      //ACCOMPANIED BY TRUSTED INDIVIDUAL
      if (type === "radio" && name === "accompanied-by-trustee") {
        setIsAccompanied(e.target.value.toUpperCase());
        isAccompaniedError = "";
      }

      //TRUSTEE INFORMATION
      if (id === "trusteeReason" && alphanum.test(e.target.value)) {
        if (e.target.value.length === 0) trusteeReasonError = "Please provide a reason";
        else if (e.target.value.length > MAX_LENGTH_TEXTAREA) trusteeReasonError = "Exceeded 1000 characters";
        else trusteeReasonError = "";

        setTrusteeReason(e.target.value);
      }

      //TRUSTEE INFORMATION
      if (id === "trusteeFullName" && alphanum.test(e.target.value)) {
        if (e.target.value.length === 0) trusteeFullNameError = "Full name is required.";
        else if (e.target.value.length > 70) trusteeFullNameError = "Full name exceeded 70 characters";
        else trusteeFullNameError = "";

        setTrusteeFullName(e.target.value);
      }

      if (id === "trusteeId") {
        if (alphanum.test(e.target.value)) {
          if (e.target.value.length === 0) trusteeIdError = "Identification number is required.";
          else if (e.target.value.length > 30) trusteeIdError = "Identification number exceeded 30 characters";
          else trusteeIdError = "";

          setTrusteeId(e.target.value);
        }
      }

      if (id === "trusteeMobileNumber" && reNumbers.test(e.target.value)) {
        if (e.target.value.length === 0) trusteeMobileNumberError = "Mobile number is required.";
        else if (e.target.value.length > 12) trusteeMobileNumberError = "Mobile number exceeded 12 characters";
        else trusteeMobileNumberError = "";

        setTrusteeMobileNumber(e.target.value);
      }

      if (id === "trusteeEmail" && alphanum.test(e.target.value)) {
        if (e.target.value.length === 0) trusteeEmailError = "Email is required.";
        else if (e.target.value.length > 30) trusteeEmailError = "Email exceeded 30 characters";
        else if (!reEmail.test(trusteeEmail)) trusteeEmailError = "Email is invalid";
        else trusteeEmailError = "";

        setTrusteeEmail(e.target.value);
      }

      if (id === "trusteeRelationship" && alphanum.test(e.target.value)) {
        if (e.target.value.length === 0) trusteeRelationshipError = "Relationship description is required";
        else if (e.target.value.length > 30)
          trusteeRelationshipError = "Relationship description exceeded 30 characters";
        else trusteeRelationshipError = "";

        setTrusteeRelationship(e.target.value);
      }

      if (id === "trusteeLanguage" && alphanum.test(e.target.value)) {
        if (e.target.value.length === 0) trusteeLanguageError = "Language is required";
        else if (e.target.value.length > 30) trusteeLanguageError = "Language exceeded 30 characters";
        else trusteeLanguageError = "";

        setTrusteeLanguage(e.target.value);
      }

      //TRUSTEE- ACKNOWLEDGE
      if (id === "trusteeAcknowledge") {
        setTrusteeAcknowledge(e.target.checked);
        trusteeAcknowledgeError = "";
      }

      calculateValidity();
    }
  }

  function handleTrusteeDobChange(date) {
    setTrusteeDOB(date);
  }

  return (
    <>
      <Box component="form" onSubmit={validateForm} noValidate>
        <Grid container spacing={2}>
          <Grid size={12}>
            <GridSectionHeader
              sectionName="Trusted Individual"
              sx={{
                p: 1,
                pl: 1.8,
                mt: 0,
                mb: 1,
                fontSize: 18,
                fontWeight: "bold",
                color: "#FFFFFF", //WHITE
                // color: "#000000", //BLACK
                // backgroundColor: "#d9d9d9",
                backgroundColor: "#10B981",
              }}
            />

            <Typography variant="body1" sx={{ mb: 2 }}>
              Appoint a Trusted Individual for <b>[{fullName}]</b>.
            </Typography>

            <Typography variant="body1">
              It is recommended for you to be accompanied by a Trusted Individual if you belong to one of the following
              profiles. If you belong to two or more of the following profiles, it is compulsary for you to be
              accompanied by a Trusted Individual.
              如果您符合以下3项标准中的任何1项，我们建议一位信托人士陪您进行这项销售咨询程序。倘若您符合以下3项标准中的任何2项，您必须有一位信托人士陪您进行这项销售咨询程序。
            </Typography>

            {/*==============================
              RECOMMEND APPOINTING TRUSTED INDIVIDUAL
            ==============================*/}
            <Typography variant="h6" gutterBottom sx={{ p: 2, my: 1, border: "1px solid", borderRadius: 5 }}>
              Please appoint a Trusted Individual because of the following reason(s):
              <List disablePadding sx={{ pl: 4, listStyleType: "disc" }}>
                <ListItem disablePadding sx={{ pl: 1, display: "list-item" }}>
                  <Typography variant="body1">
                    Clients who cannot communicate, read or write in English / 不善英文 (口语或读写)
                  </Typography>
                </ListItem>
                <ListItem disablePadding sx={{ pl: 1, display: "list-item" }}>
                  <Typography variant="body1">
                    Clients who do not have minimum of "N or "O" levels / 教育程度未达到 GCE 'N' 或 'O' 水准
                  </Typography>
                </ListItem>
              </List>
              {/*==============================
                ACKNOWLEDGEMENT - SELECTED CLIENT
              ==============================*/}
              <GridCheckbox
                id="selectedClient"
                key="selectedClient"
                columnSize="8"
                label="I acknowledge that I have been identified a Selected Client"
                checked={isSelectedClient}
                onChange={handleChange}
                required
                error={isSelectedClientError}
              />
              {isSelectedClientError ? (
                <FormHelperText error={isSelectedClientError}>{isSelectedClientError}</FormHelperText>
              ) : (
                ""
              )}
            </Typography>

            {/*==============================
              TRUSTED INDIVIDUAL - DECLARE AVAILABILITY
             ==============================*/}
            <Typography variant="body1" sx={{ pt: 2 }}>
              Would the client like to be accompanied by a Trusted Individual?
            </Typography>

            <RadioGroup
              id="accompanied-by-trustee"
              name="accompanied-by-trustee"
              defaultValue=""
              value={isAccompanied}
              onChange={handleChange}
            >
              <FormControl>
                <Stack direction="row">
                  <FormControlLabel
                    id="accompaniedByTrustedIndividualYes"
                    value="YES"
                    control={<Radio />}
                    label="Yes"
                  />

                  <FormControlLabel id="accompaniedByTrustedIndividualNo" value="NO" control={<Radio />} label="No" />
                </Stack>
                {Boolean(isAccompaniedError) ? (
                  <FormHelperText error={isAccompaniedError}>{isAccompaniedError}</FormHelperText>
                ) : (
                  ""
                )}
              </FormControl>
            </RadioGroup>
          </Grid>

          {/*==============================
           TRUSTED INDIVIDUAL - ACKNOWLEDGEMENT
          ==============================*/}
          <Grid size={12}>
            {isAccompanied === "NO" ? (
              <>
                <Typography variant="body1">
                  The selected client has declined to have a Trusted Individual, please state any additional safeguards
                  performed.
                </Typography>

                <TextareaAutosize
                  id="trusteeReason"
                  placeholder="Type here..."
                  style={{ width: "100%" }}
                  minRows={5}
                  onChange={handleChange}
                />
                {trusteeReasonError ? (
                  <FormHelperText error={trusteeReasonError}>{trusteeReasonError}</FormHelperText>
                ) : (
                  ""
                )}
              </>
            ) : (
              ""
            )}
          </Grid>

          {/*==============================
          TRUSTED INDIVIDUAL - DETAILS
          ==============================*/}
          {isAccompanied === "YES" ? (
            <>
              <GridSectionHeader sectionName="Trusted Individual's Information & Declaration" />
              {/* FULLNAME*/}
              <GridTextfield
                id="trusteeFullName"
                columnSize="6"
                label="Full Name (As per NRIC/Passport"
                currState={trusteeFullName}
                onChange={handleChange}
                error={trusteeFullNameError}
                helperText={trusteeFullNameError}
              />
              {/* DATE OF BIRTH */}
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    id="trusteeDob"
                    format="DD MMM YYYY"
                    label="Data of Birth"
                    value={trusteeDOB}
                    onChange={(newDate) => handleTrusteeDobChange(newDate)}
                  />
                </LocalizationProvider>
              </Grid>

              {/* IDENTIFICATION TYPE */}
              <GridDropdown
                id="trusteeIdType"
                key="trusteeIdType"
                columnSize="3"
                label="Identity Type"
                defaultValue=""
                value=""
                currState={trusteeIdType}
                onChange={handleChange}
                data={IDENTITY_TYPE_DATA}
                error={trusteeIdTypeError}
              />

              {/* IDENTIFICATION */}
              <GridTextfield
                id="trusteeId"
                key="trusteeId"
                columnSize="3"
                label="Identification Number"
                currState={trusteeId}
                onChange={handleChange}
                error={trusteeIdError}
                helperText={trusteeIdError}
              />

              {/* COUNTRY OF NATIONALITY */}
              <GridDropdown
                id="trusteeNationality"
                key="trusteeNationality"
                columnSize="6"
                label="Country of Nationality"
                defaultValue=""
                value=""
                currState={trusteeNationality}
                onChange={handleChange}
                data={countryPhoneCode}
                dataType="country"
                error={trusteeNationalityError}
              />

              {/*MOBILE - COUNTRY CODE*/}
              <GridDropdown
                id="trusteeMobileCode"
                key="trusteeMobileCode"
                columnSize="3"
                label="Country Code"
                defaultValue=""
                value=""
                currState={trusteeMobileCode}
                onChange={handleChange}
                data={countryPhoneCode}
                dataType="countryCode"
                error={trusteeMobileCodeError}
              />

              {/* MOBILE - NUMBER*/}
              <GridTextfield
                id="trusteeMobileNumber"
                key="trusteeMobileNumber"
                columnSize="3"
                label="Mobile Number"
                currState={trusteeMobileNumber}
                onChange={handleChange}
                error={trusteeMobileNumberError}
                helperText={trusteeMobileNumberError}
              />

              {/* EMAIL */}
              <GridTextfield
                id="trusteeEmail"
                key="trusteeEmail"
                columnSize="6"
                label="Email"
                currState={trusteeEmail}
                onChange={handleChange}
                error={trusteeEmailError}
                helperText={trusteeEmailError}
              />

              {/* TRUSTEE RELATIONSHIP */}
              <GridTextfield
                id="trusteeRelationship"
                key="trusteeRelationship"
                columnSize="6"
                label="Relationship with client"
                currState={trusteeRelationship}
                onChange={handleChange}
                error={trusteeRelationshipError}
                helperText={trusteeRelationshipError}
              />

              {/* LANGUAGE USED */}
              <GridTextfield
                id="trusteeLanguage"
                key="trusteeLanguage"
                columnSize="6"
                label="Language/dialect used for explanation"
                currState={trusteeLanguage}
                onChange={handleChange}
                error={trusteeLanguageError}
                helperText={trusteeLanguageError}
              />

              {/*==============================
               TRUSTEE - DECLARATION
              ==============================*/}
              <Typography variant="body1" gutterBottom sx={{ p: 2, my: 1, border: "1px solid", borderRadius: 5 }}>
                <GridCheckbox
                  id="trusteeAcknowledge"
                  columnSize="12"
                  label="I, the appointed Trusted Individual, acknowledged that the information I have provided above is true, 
            and that I will be present alongside the Selected Client during the sales process."
                  disabled={false}
                  onChange={handleChange}
                />
                {trusteeAcknowledgeError ? (
                  <FormHelperText error={trusteeAcknowledgeError}>{trusteeAcknowledgeError}</FormHelperText>
                ) : (
                  ""
                )}
              </Typography>
            </>
          ) : (
            ""
          )}

          <PageNavigator validator={validateForm} isValid={isValid} />
        </Grid>
      </Box>
    </>
  );
}
